import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
 
export default function FormSelectTW({
  value,
  onChange,
  options = [],
  placeholder = 'Select…',
  disabled = false,
  className = '',
  renderItem, // optional: (opt) => node
}) {
  const triggerRef = useRef(null);
  const menuRef = useRef(null); // <-- NEW: track menu element
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const [mounted, setMounted] = useState(false);
 
  useEffect(() => setMounted(true), []);
 
  // Position the menu inside the nearest [data-modal-boundary]
// Prefer right side on desktop; otherwise fall back to down/up with clamped height.
const positionMenu = () => {
  const el = triggerRef.current;
  if (!el) return;
 
  const trigger = el.getBoundingClientRect();
  const modalEl = el.closest('[data-modal-boundary]');
  const boundary = (modalEl || document.body).getBoundingClientRect();
 
  const gap = 6;
  const vw = window.innerWidth;
  const isDesktop = vw >= 800;
 
  // Available space *inside* the boundary
  const spaceRight = boundary.right - trigger.right - gap;
  const spaceLeft  = trigger.left - boundary.left - gap;
  const spaceBelow = boundary.bottom - trigger.bottom - gap;
  const spaceAbove = trigger.top - boundary.top - gap;
 
  // Desired dimensions
  const desiredWidth = Math.min(trigger.width, 320);
  const maxWidth = Math.min(trigger.width, boundary.width - 16);
  const maxDownHeight = Math.max(120, Math.min(360, spaceBelow));
  const maxUpHeight   = Math.max(120, Math.min(360, spaceAbove));
 
  // Try RIGHT (desktop only) if we have at least ~trigger width available.
  if (isDesktop && spaceRight >= Math.max(220, trigger.width * 0.9)) {
    // Vertical fit inside boundary
    const top = Math.max(
      boundary.top + gap,
      Math.min(trigger.top, boundary.bottom - gap - 200) // leave room for min height
    );
    const availableDown = boundary.bottom - top - gap;
    setMenuStyle({
      position: 'fixed',
      left: trigger.right + gap,
      top,
      width: desiredWidth,
      maxWidth,
      maxHeight: Math.max(160, Math.min(400, availableDown)),
      zIndex: 4000,
    });
    return;
  }
 
  // Else DOWN if it fits better than UP
  const openDown = spaceBelow >= spaceAbove;
  if (openDown) {
    setMenuStyle({
      position: 'fixed',
      left: Math.max(boundary.left + 8, Math.min(trigger.left, boundary.right - maxWidth - 8)),
      top: trigger.bottom + gap,
      width: trigger.width,
      maxWidth,
      maxHeight: maxDownHeight,
      zIndex: 4000,
    });
    return;
  }
 
  // Else UP
  {
  const left = Math.max(
    boundary.left + 8,
    Math.min(trigger.left, boundary.right - maxWidth - 8)
  );
 
  setMenuStyle({
    position: 'fixed',
    left,
    // distance from viewport bottom so the menu's bottom hugs the trigger
    bottom: window.innerHeight - trigger.top + gap,
    width: trigger.width,
    maxWidth,
    // clamp height to what's available above inside the modal
    maxHeight: Math.max(120, Math.min(360, spaceAbove)),
    zIndex: 4000,
  });
  return;
}
  };
 
  useLayoutEffect(() => {
    if (!open) return;
    positionMenu();
    const onScroll = () => positionMenu();
    const onResize = () => positionMenu();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);
 
  // Proper click-away that does NOT close when clicking inside the menu
  useEffect(() => {
    if (!open) return;
 
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onClickAway = (e) => {
      if (!triggerRef.current) return;
      // ignore clicks on trigger or inside the menu
      if (triggerRef.current.contains(e.target)) return;
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setOpen(false);
    };
 
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClickAway);
    document.addEventListener('touchstart', onClickAway);
 
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClickAway);
      document.removeEventListener('touchstart', onClickAway);
    };
  }, [open]);
 
  const currentLabel = (() => {
    const v = value == null ? '' : String(value);
    const m = options.find((o) => String(o) === v);
    return m ? String(m) : v; // fall back to raw value if not in list
  })();
 
  return (
    <>
      {/* Trigger (looks like an input/select) */}
      <button
        type="button"
        ref={triggerRef}
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        className={[
          'w-full text-left border rounded-lg px-3 py-2 bg-white',
          'focus:outline-none focus:ring-2 focus:ring-blue-500',
          disabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'hover:bg-gray-50',
          'relative',
          className,
        ].join(' ')}
      >
        <span className={currentLabel ? 'text-gray-900' : 'text-gray-400'}>
          {currentLabel || placeholder}
        </span>
        <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
          ▾
        </span>
      </button>
 
      {/* Menu (portal) */}
      {mounted &&
        open &&
        createPortal(
          <div
            ref={menuRef}
            style={menuStyle}
            className="max-h-[50vh] overflow-auto rounded-lg border border-gray-200 bg-white shadow-xl"
            // prevent events from reaching document (extra safety)
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
            {options.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-400">No options</div>
            ) : (
              <ul className="py-1">
                {options.map((opt) => {
                  const v = String(opt).trim();
                  const isActive = String(value ?? '').trim() === v;
                  return (
                    <li key={v}>
                      <button
                        type="button"
                        onClick={() => {
                          onChange?.(opt);
                          setOpen(false);
                        }}
                        className={[
                        'w-full text-left px-3 py-2 text-sm transition-colors',
                        isActive
                            ? 'bg-blue-50 text-blue-700'
                            : 'hover:bg-blue-50 hover:text-blue-700 active:bg-blue-100 active:text-blue-800',
                        ].join(' ')}
                      >
                        <span className="inline-flex items-center gap-2">
                          {renderItem ? renderItem(opt) : v}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>,
          document.body
        )}
    </>
  );
}